import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Mail, Check, AlertCircle, Loader } from 'lucide-react';
import { BRAND } from '../../config/branding';

export default function EmailPreferencesPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [preferences, setPreferences] = useState(null);
  
  const [formData, setFormData] = useState({
    emailFrequency: 'immediate',
    topics: [],
  });

  const availableTopics = [
    { id: 'consulting', label: 'Consulting Services', description: 'Updates about consulting offerings' },
    { id: 'staffing', label: 'Staffing Services', description: 'Job opportunities and staffing updates' },
    { id: 'lms', label: 'Training & LMS', description: 'Learning and training course updates' },
    { id: 'alerts', label: 'Alerts & Updates', description: 'Important alerts and system updates' },
  ];

  const frequencyOptions = [
    { value: 'immediate', label: 'Immediately', description: 'Receive emails as soon as they\'re sent' },
    { value: 'daily', label: 'Daily Digest', description: 'Receive one email per day' },
    { value: 'weekly', label: 'Weekly Digest', description: 'Receive one email per week' },
    { value: 'never', label: 'Never', description: 'Unsubscribe from all emails' },
  ];

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${import.meta.env.VITE_API_URL}/drip/preferences/${token}`);
        
        if (!response.ok) {
          throw new Error('Invalid or expired preference link');
        }
        
        const data = await response.json();
        setPreferences(data);
        setFormData({
          emailFrequency: data.preferences?.emailFrequency || 'immediate',
          topics: data.preferences?.topics || [],
        });
      } catch (err) {
        setError(err.message || 'Failed to load preferences');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPreferences();
    }
  }, [token]);

  const handleFrequencyChange = (newFrequency) => {
    setFormData(prev => ({
      ...prev,
      emailFrequency: newFrequency,
    }));
    setSuccess(false);
  };

  const handleTopicToggle = (topicId) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.includes(topicId)
        ? prev.topics.filter(t => t !== topicId)
        : [...prev.topics, topicId],
    }));
    setSuccess(false);
  };

  const handleUnsubscribeAll = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from all emails?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/drip/preferences/${token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unsubscribe: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to unsubscribe');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/drip/preferences/${token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email Preferences</h1>
              <p className="text-gray-600">{preferences?.email}</p>
            </div>
          </div>
          <p className="text-gray-700">
            Manage how you receive emails from {BRAND.name}. You can adjust your email frequency and choose which topics interest you.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Error</p>
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-900">Preferences Updated</p>
              <p className="text-green-800 text-sm">Your preferences have been saved. Redirecting...</p>
            </div>
          </div>
        )}

        {/* Email Frequency Section */}
        <div className="bg-white rounded-xl shadow-sm border p-8 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Frequency</h2>
          <p className="text-gray-600 text-sm mb-6">How often would you like to receive emails from us?</p>
          
          <div className="space-y-3">
            {frequencyOptions.map(option => (
              <label key={option.value} className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" style={{
                borderColor: formData.emailFrequency === option.value ? '#3b82f6' : '#e5e7eb',
                backgroundColor: formData.emailFrequency === option.value ? '#eff6ff' : 'white',
              }}>
                <input
                  type="radio"
                  name="emailFrequency"
                  value={option.value}
                  checked={formData.emailFrequency === option.value}
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                  disabled={saving}
                  className="w-4 h-4 text-blue-600"
                />
                <div className="ml-4 flex-1">
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Topics Section (only show if not set to 'never') */}
        {formData.emailFrequency !== 'never' && (
          <div className="bg-white rounded-xl shadow-sm border p-8 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Topics</h2>
            <p className="text-gray-600 text-sm mb-6">Select which topics interest you. Leave all unchecked to receive all topics.</p>
            
            <div className="grid gap-3">
              {availableTopics.map(topic => (
                <label key={topic.id} className="flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.topics.includes(topic.id)}
                    onChange={() => handleTopicToggle(topic.id)}
                    disabled={saving}
                    className="w-4 h-4 text-blue-600 rounded mt-1"
                  />
                  <div className="ml-4 flex-1">
                    <p className="font-medium text-gray-900">{topic.label}</p>
                    <p className="text-sm text-gray-600">{topic.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="flex gap-4">
            <button
              onClick={handleSavePreferences}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
            <button
              onClick={handleUnsubscribeAll}
              disabled={saving}
              className="flex-1 bg-red-50 hover:bg-red-100 disabled:bg-gray-100 text-red-600 hover:text-red-700 disabled:text-gray-400 font-semibold py-3 px-6 rounded-lg transition-colors border border-red-200"
            >
              {saving ? 'Processing...' : 'Unsubscribe Completely'}
            </button>
          </div>
          <p className="text-gray-600 text-sm mt-4 text-center">
            You can update your preferences anytime using the link in our emails.
          </p>
        </div>
      </div>
    </div>
  );
}
